import { useEffect, useMemo, useState } from "react";
import useCountryLeagues from "@/hooks/useCountryLeagues";
import { useCompConfig } from "@/context/CompConfigContext";
import { buildThemeConfig } from "@/components/LichThiDauFull/buildThemeConfig";
import { KbvPluginService } from "@/services/kbvPluginService";

async function fetchLeagueStandings(leagueId) {
  if (!leagueId) return null;
  try {
    const data = await KbvPluginService.getLeagueStandings(leagueId);
    if (!data || Array.isArray(data)) return null;
    const result = data.result;
    if (!result) return null;
    return { ...result, colorInfos: data.colorInfos || [] };
  } catch (error) {
    console.error(error);
    return null;
  }
}

function hasStandingContent(standingsRes) {
  if (!standingsRes) return false;
  return (
    (Array.isArray(standingsRes.roundScoreItems) &&
      standingsRes.roundScoreItems.length > 0) ||
    (Array.isArray(standingsRes.totalStandings) &&
      standingsRes.totalStandings.length > 0)
  );
}

function defaultLeagueId(leagues) {
  if (!leagues.length) return null;
  const hot = leagues.find((l) => Number(l?.isHot) === 1);
  const pick = hot || leagues[0];
  const id = pick?.id;
  return id != null ? String(id) : null;
}

export default function useBangXepHangByCountry(countrySlug) {
  const compConfig = useCompConfig();
  const themeStyle = buildThemeConfig(compConfig, compConfig.hasDataConfig);
  const [selectedId, setSelectedId] = useState(null);
  const [standingsMap, setStandingsMap] = useState({});
  const [standingsLoading, setStandingsLoading] = useState(false);

  const {
    leagues,
    loading: countryLoading,
    error: countryError,
  } = useCountryLeagues(countrySlug);

  useEffect(() => {
    setSelectedId(null);
    setStandingsMap({});
  }, [countrySlug]);

  useEffect(() => {
    if (!leagues.length) {
      setSelectedId(null);
      return;
    }
    setSelectedId((prev) => {
      if (prev && leagues.some((l) => String(l?.id) === String(prev))) return prev;
      return defaultLeagueId(leagues);
    });
  }, [leagues]);

  const activeLeagues = useMemo(() => {
    if (!selectedId || !leagues.length) return [];
    const league = leagues.find((l) => String(l?.id) === String(selectedId));
    return league ? [league] : [];
  }, [leagues, selectedId]);

  const activeKey = activeLeagues.map((l) => String(l.id)).join(",");

  useEffect(() => {
    if (!activeLeagues.length) {
      setStandingsMap({});
      setStandingsLoading(false);
      return undefined;
    }

    let cancelled = false;
    setStandingsLoading(true);

    (async () => {
      const entries = await Promise.all(
        activeLeagues.map(async (league) => {
          const res = await fetchLeagueStandings(league.id);
          return [String(league.id), res];
        }),
      );
      if (cancelled) return;
      const next = {};
      for (const [id, res] of entries) next[id] = res;
      setStandingsMap(next);
      setStandingsLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [activeKey]);

  const waiting = !!countrySlug && (countryLoading || standingsLoading);
  const failed =
    !!countrySlug && !countryLoading && (!!countryError || !leagues.length);

  const leaguesWithContent = activeLeagues.filter((league) =>
    hasStandingContent(standingsMap[String(league.id)]),
  );

  return {
    themeStyle,
    leagues,
    selectedId,
    setSelectedId,
    activeLeagues,
    standingsMap,
    leaguesWithContent,
    waiting,
    failed,
    countryLoading,
  };
}
